import { useCallback, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getLocalStorage } from '@trinity/chat-components';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import {
  ActiveBrandContext,
  SetActiveBrandContext,
  UserBrandsContext,
} from '@trinity/chat-components/containers/UserBrands/UserBrandsContext.js';
import { useAppReadyState } from '../AppReadyState';

const ACTIVE_BRAND_STORAGE_KEY = 'insightsedge:active-brand-identifier';
const storage = getLocalStorage();

type Props = ChildrenProps;

export const UserBrandsContainer = (props: Props) => {
  const app = useAppReadyState();
  const [selectedBrandIdentifier, setSelectedBrandIdentifier] = useState<string | null>(() =>
    storage.getItem(ACTIVE_BRAND_STORAGE_KEY)
  );

  const apiClient = useChatApiClient();

  const q = useQuery({
    queryKey: ['get:user-brands'],
    queryFn: async () => {
      const res = await apiClient.users.getUserBrands().then((r) => r.brands);

      if (!res.length) {
        throw new Error('No brands found for the user');
      }

      return res;
    },
    enabled: app.authenticated,
    initialData: [],
    placeholderData: keepPreviousData,
  });

  const activeBrandIdentifier = useMemo(() => {
    if (!q.data.length) return null;

    const isSelectedBrandValid =
      selectedBrandIdentifier && q.data.some((b) => b.identifier === selectedBrandIdentifier);

    if (!isSelectedBrandValid) {
      storage.removeItem(ACTIVE_BRAND_STORAGE_KEY);
    }

    return isSelectedBrandValid ? selectedBrandIdentifier : q.data[0].identifier;
  }, [selectedBrandIdentifier, q.data]);

  const setAndPersistActiveBrand = useCallback<React.Dispatch<React.SetStateAction<string>>>((value) => {
    setSelectedBrandIdentifier((prev) => {
      const next = typeof value === 'function' ? value(prev ?? '') : value;
      storage.setItem(ACTIVE_BRAND_STORAGE_KEY, next);
      return next;
    });
  }, []);

  return (
    <ActiveBrandContext.Provider value={activeBrandIdentifier}>
      <SetActiveBrandContext.Provider value={setAndPersistActiveBrand}>
        <UserBrandsContext.Provider value={q.data}>{props.children}</UserBrandsContext.Provider>
      </SetActiveBrandContext.Provider>
    </ActiveBrandContext.Provider>
  );
};
